import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { companyInfo, DOZZ_LOGO_URL, DOZZ_GOLD_LOGO_URL, VIDYUTSAFE_LOGO_URL, brands } from '../../data/mock';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/#about' },
  { label: 'Products', href: '/#products' },
  { label: 'Brands', href: '/#brands' },
  { label: 'Certifications', href: '/#certifications' },
  { label: 'Clients', href: '/#clients' },
  { label: 'Contact', href: '/#inquiry' },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
  };

  const scrollToInquiry = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById('inquiry');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById('inquiry');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-navy-950 text-navy-200 py-2 text-sm hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`mailto:${companyInfo.emails[0]}`} className="flex items-center gap-1.5 hover:text-white">
              <Mail className="w-3.5 h-3.5" />
              {companyInfo.emails[0]}
            </a>
            <a href={`tel:${companyInfo.phones[0]}`} className="flex items-center gap-1.5 hover:text-white">
              <Phone className="w-3.5 h-3.5" />
              {companyInfo.phones[0]}
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-navy-400">|</span>
            <span className="font-medium text-ind-red-400">ISO 9001:2015 Certified</span>
            <span className="text-navy-400">|</span>
            <span>BIS Approved</span>
            <span className="text-navy-400">|</span>
            <span>Est. 1984</span>
          </div>
        </div>
      </div>

      {/* Brands Strip - Subtle display of all 5 brands */}
      <div className="bg-navy-50 border-b border-navy-100 py-1.5 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-8">
            <span className="text-xs text-navy-400 font-medium uppercase tracking-wider">Our Brands:</span>
            {brands.map((brand, idx) => (
              <span key={brand.name} className="flex items-center gap-1.5">
                <span 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: brand.color }}
                />
                <span className="text-xs font-semibold text-navy-700">{brand.name}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-navy-950/5 border-b border-navy-100'
            : 'bg-white border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img src={DOZZ_LOGO_URL} alt="DOZZ Logo" className="h-10 w-auto rounded-md object-contain" />
              <div className="hidden sm:block">
                <p className="font-heading font-bold text-navy-900 text-base leading-tight">V.R. Enterprises</p>
                <p className="text-[11px] text-navy-500 leading-tight">Industrial Hoses & Safety</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="px-3 py-2 text-sm font-medium text-navy-700 hover:text-ind-red-600 rounded-md hover:bg-navy-50 transition-colors duration-200"
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a href={`tel:${companyInfo.phones[0]}`} className="flex items-center gap-1.5 text-sm text-navy-600 hover:text-navy-900 font-medium">
                <Phone className="w-4 h-4" />
                <span className="hidden xl:inline">{companyInfo.phones[0]}</span>
              </a>
              <Button
                onClick={scrollToInquiry}
                className="bg-ind-red-600 hover:bg-ind-red-700 text-white font-semibold px-5 h-10 rounded-lg shadow-sm hover:shadow-md"
              >
                Request Quote
              </Button>
            </div>

            {/* Mobile Menu */}
            <div className="lg:hidden flex items-center gap-2">
              <Button
                onClick={scrollToInquiry}
                size="sm"
                className="bg-ind-red-600 hover:bg-ind-red-700 text-white text-xs px-3 h-8 rounded-md"
              >
                Get Quote
              </Button>
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-navy-900">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-white p-0">
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-8">
                      <img src={DOZZ_LOGO_URL} alt="DOZZ Logo" className="h-10 w-auto rounded-md" />
                      <div>
                        <p className="font-heading font-bold text-navy-900">V.R. Enterprises</p>
                        <p className="text-xs text-navy-500">Since 1984</p>
                      </div>
                    </div>
                    <nav className="flex flex-col gap-1">
                      {navLinks.map((link) => (
                        <button
                          key={link.label}
                          onClick={() => handleNavClick(link.href)}
                          className="text-left px-4 py-3 text-navy-700 hover:text-ind-red-600 hover:bg-navy-50 rounded-lg font-medium transition-colors duration-200"
                        >
                          {link.label}
                        </button>
                      ))}
                    </nav>
                    <div className="mt-8 pt-6 border-t border-navy-100 space-y-3">
                      <a href={`tel:${companyInfo.phones[0]}`} className="flex items-center gap-2 text-sm text-navy-600">
                        <Phone className="w-4 h-4" />
                        {companyInfo.phones[0]}
                      </a>
                      <a href={`mailto:${companyInfo.emails[0]}`} className="flex items-center gap-2 text-sm text-navy-600">
                        <Mail className="w-4 h-4" />
                        {companyInfo.emails[0]}
                      </a>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
